import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Toggle } from '@/components/forms/Checkbox'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { FormField } from '@/components/forms/FormField'
import { closeModal } from '@/reducers/ui/ui.redux'
import { updateSecuritySettings } from '@/reducers/organizations/organizations.actions'

export const SECURITY_SETTINGS_MODAL_ID = 'organizations/security-settings'

/**
 * Edit modal for the sensitive org security settings: unredacted, PCI
 * compliance, and SSN redaction. Only rendered/opened for allowlisted users
 * (see fetchSecuritySettingsAccess); core-service also enforces on write.
 *
 * SSN redaction only takes effect with PCI compliance, so the SSN toggle is
 * disabled until compliance is on, and compliance can't be turned off while
 * SSN redaction is on (the backend enforces both, returning a 400 that we
 * surface via toast).
 */
export const OrganizationSecuritySettingsModal = ({ organization }) => {
  const dispatch = useDispatch()
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const isOpen = currentlyOpenModalId === SECURITY_SETTINGS_MODAL_ID

  const [unredactedEnabled, setUnredactedEnabled] = useState(false)
  const [requiresCompliance, setRequiresCompliance] = useState(false)
  const [redactSsn, setRedactSsn] = useState(false)
  const [saving, setSaving] = useState(false)

  // Re-seed the toggles from the org each time the modal opens.
  useEffect(() => {
    if (isOpen && organization) {
      setUnredactedEnabled(Boolean(organization.unredacted_enabled))
      setRequiresCompliance(Boolean(organization.requires_compliance))
      setRedactSsn(Boolean(organization.redact_ssn))
    }
  }, [isOpen, organization])

  const handleClose = () => dispatch(closeModal())

  const handleSave = () => {
    setSaving(true)
    dispatch(
      updateSecuritySettings({
        organizationId: organization.id,
        settings: {
          unredacted_enabled: unredactedEnabled,
          requires_compliance: requiresCompliance,
          redact_ssn: redactSsn,
        },
        onSuccess: () => {
          setSaving(false)
          handleClose()
        },
        onError: () => setSaving(false),
      })
    )
  }

  if (!organization) {
    return null
  }

  // The Balto org (org 1) must never gain persistent unredacted access; mirror
  // the core-service guard so the toggle is unavailable here too.
  const isBaltoOrg = organization.id === 1

  return (
    <BasicModal
      show={isOpen}
      onClose={handleClose}
      title={`Security Settings: ${organization.name}`}
      size="small"
      footer={
        <>
          <Button onClick={handleClose} disabled={saving} data-testid="cancel-security-settings">
            Cancel
          </Button>
          <Button brand onClick={handleSave} loading={saving} data-testid="save-security-settings">
            Save
          </Button>
        </>
      }
    >
      <Banner informative>
        These settings are highly sensitive. SSN redaction only takes effect when PCI compliance is
        enabled.
      </Banner>

      <div className="field-stack">
        <FormField>
          <Toggle
            label="Unredacted"
            checked={unredactedEnabled}
            disabled={isBaltoOrg}
            onChange={() => setUnredactedEnabled((value) => !value)}
            data-testid="unredacted-toggle"
          />
          {isBaltoOrg && (
            <div className="form-field-hint">Not available for the Balto organization.</div>
          )}
        </FormField>

        <FormField>
          <Toggle
            label="Requires PCI compliance"
            checked={requiresCompliance}
            disabled={redactSsn}
            onChange={() => setRequiresCompliance((value) => !value)}
            data-testid="requires-compliance-toggle"
          />
          {redactSsn && (
            <div className="form-field-hint">
              Disable SSN redaction before turning off PCI compliance.
            </div>
          )}
        </FormField>

        <FormField>
          <Toggle
            label="SSN redaction"
            checked={redactSsn}
            // Always allow turning SSN redaction OFF, even for an org already
            // in the inconsistent (compliance off, redaction on) state, so the
            // modal can't deadlock; only block enabling it without compliance.
            disabled={!requiresCompliance && !redactSsn}
            onChange={() => setRedactSsn((value) => !value)}
            data-testid="redact-ssn-toggle"
          />
          {!requiresCompliance && !redactSsn && (
            <div className="form-field-hint">Enable PCI compliance first.</div>
          )}
        </FormField>
      </div>
    </BasicModal>
  )
}

export default OrganizationSecuritySettingsModal
